import { normalizeSeverity } from "./toastColors";

// Simple pub-sub listener for toasts
const listeners = new Set();

// Deduplication cache: key -> timestamp
const recentToasts = new Map();
const DEDUP_WINDOW_MS = 2500;

const isDuplicate = (message, severity) => {
  if (!message) return true;
  const key = `${severity}:${String(message).trim()}`;
  const now = Date.now();
  const lastTime = recentToasts.get(key);

  // Clean old entries
  for (const [k, timestamp] of recentToasts.entries()) {
    if (now - timestamp > DEDUP_WINDOW_MS) {
      recentToasts.delete(k);
    }
  }

  if (lastTime && now - lastTime < DEDUP_WINDOW_MS) {
    return true;
  }

  recentToasts.set(key, now);
  return false;
};

export const extractMessage = (data, fallback = "") => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data?.status?.value && typeof data.status.value === "string") {
    return data.status.value;
  }
  if (data?.message && typeof data.message === "string") {
    return data.message;
  }
  if (data?.msg && typeof data.msg === "string") {
    return data.msg;
  }
  if (data?.error && typeof data.error === "string") {
    return data.error;
  }
  if (data?.statusText && typeof data.statusText === "string") {
    return data.statusText;
  }
  return fallback;
};

export const extractErrorMessage = (error, fallback = "An unexpected error occurred.") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  // Axios response error
  const resData = error?.response?.data;
  if (resData) {
    const extracted = extractMessage(resData);
    if (extracted) return extracted;
  }

  if (error?.message && typeof error.message === "string") {
    return error.message;
  }

  return fallback;
};

export const mapStatusCodeToSeverity = (status) => {
  const code = Number(status);
  if (isNaN(code) || code === 0) return "error";
  if (code >= 200 && code < 300) return "success";
  if (code === 400 || code === 422 || code === 409) return "warning";
  if (code === 401 || code === 403 || code === 404) return "error";
  if (code >= 500) return "error";
  if (code >= 400 && code < 500) return "warning";
  return "info";
};

export const toast = {
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  show({ message, title, severity = "info", duration = 4000, force = false }) {
    if (!message) return;
    const normSeverity = normalizeSeverity(severity);
    if (!force && isDuplicate(message, normSeverity)) {
      return;
    }
    const payload = {
      id: Date.now() + Math.random(),
      message,
      title,
      severity: normSeverity,
      duration,
    };
    listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (err) {
        console.error("Toast listener error:", err);
      }
    });
  },

  success(message, duration = 4000, title = "Success") {
    this.show({ message, title, severity: "success", duration });
  },

  info(message, duration = 4000, title = "Info") {
    this.show({ message, title, severity: "info", duration });
  },

  warning(message, duration = 4000, title = "Warning") {
    this.show({ message, title, severity: "warning", duration });
  },

  error(message, duration = 4000, title = "Error") {
    this.show({ message, title, severity: "error", duration });
  },

  danger(message, duration = 4000, title = "Error") {
    this.show({ message, title, severity: "error", duration });
  },

  /**
   * Automatically handles an API response according to HTTP status code and custom application codes.
   */
  fromResponse(response, defaultSuccess = "Operation completed successfully") {
    if (!response) return;

    // Check custom application status code in response body if present
    const appCode = response?.data?.status?.code;
    const httpStatus = response?.status || 200;
    const code = appCode !== undefined && appCode !== null ? Number(appCode) : httpStatus;

    const message =
      extractMessage(response?.data) ||
      (code >= 200 && code < 300 ? defaultSuccess : "Operation status updated");

    const severity = mapStatusCodeToSeverity(code);
    this.show({ message, severity });
  },

  /**
   * Automatically handles an API error or exception.
   */
  fromError(error, defaultError = "Operation failed. Please try again.") {
    if (!error) return;

    const httpStatus = error?.response?.status;
    const appCode = error?.response?.data?.status?.code;
    const code = httpStatus || appCode || 500;

    const message = extractErrorMessage(error, defaultError);
    const severity = mapStatusCodeToSeverity(code);
    this.show({ message, severity });
  },
};

export default toast;
